"use client";

import { useCallback, useState, useEffect } from "react";
import type { RecordingEngineReturn } from "./use-recording-engine";
// Do NOT redeclare MAX_RECORDING_SECONDS here, the engine's startTimer is the canonical auto-stop.
// Redeclaring it causes a separate timeout that double-stops the recorder and throws InvalidStateError.
import { MAX_RECORDING_SECONDS, awaitSocketReady } from "./use-recording-engine";
import type { RecordingEngineOptions } from "../types";
import { getRecordingErrorMessage } from "../utils/error-messages";
import { getSupportedAudioMime } from "../utils/mime-negotiation";
import { createOnDataAvailable } from "../utils/recorder-handlers";

export interface UseAudioRecordingOptions extends RecordingEngineOptions {
  onDataAvailable?: (data: ArrayBuffer) => void;
  videoQuality?: string; // unused for audio; kept for interface parity
  getSocketState?: () => boolean;
  /** When awaitSocketReady times out and this returns a non-empty string, that string is shown instead of the generic timeout message. */
  getSocketError?: () => string | null;
}

export interface UseAudioRecordingReturn extends RecordingEngineReturn {
  startRecording: () => Promise<void>;
  stopRecording: () => void;
}

// Avoid `exact` values, OverconstrainedError on constrained or virtual devices.
const AUDIO_CONSTRAINTS: MediaTrackConstraints = {
  echoCancellation: true,
  noiseSuppression: true,
  sampleRate: 48000,
};

export { getSupportedAudioMime };

/**
 * Lazy-load audio-recorder-polyfill only on iOS Safari where native MediaRecorder
 * doesn't support audio/webm. Assigns to window.MediaRecorder so `new MediaRecorder()`
 * picks up the polyfill transparently. Dynamic import is only evaluated when needed.
 */
export async function ensureMediaRecorderPolyfill(): Promise<void> {
  if (
    typeof MediaRecorder !== "undefined" &&
    MediaRecorder.isTypeSupported("audio/webm")
  ) {
    return;
  }
  const { default: AudioRecorderPolyfill } = await import(
    "audio-recorder-polyfill"
  );
  if (typeof window !== "undefined") {
    (window as Window & { MediaRecorder?: unknown }).MediaRecorder =
      AudioRecorderPolyfill;
  }
}

export function useAudioRecording(
  engine: RecordingEngineReturn,
  isActive: boolean,
  options?: UseAudioRecordingOptions,
): UseAudioRecordingReturn {
  // localError lets this hook override the displayed error without touching engine state.
  const [localError, setLocalError] = useState<string | null>(null);

  // Keep localError in sync with engine mic-health errors.
  useEffect(() => {
    if (
      engine.error?.startsWith(
        "Microphone disconnected, recording paused. Reconnect and tap Resume.",
      )
    ) {
      setLocalError(engine.error);
    } else if (!engine.error) {
      setLocalError(null);
    }
  }, [engine.error]);

  // Cleanup mic-health monitor on unmount.
  useEffect(() => {
    return () => {
      engine.stopMicHealthMonitor();
    };
    // engine ref is stable; effect only needs to run on unmount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startRecording = useCallback(async () => {
    if (!isActive) return;
    try {
      await ensureMediaRecorderPolyfill();

      // Include deviceId only when the user has explicitly selected a microphone;
      // omitting it lets the browser fall back to the OS default device.
      const audioConstraints: MediaTrackConstraints = engine.selectedAudioDevice
        ? { ...AUDIO_CONSTRAINTS, deviceId: { exact: engine.selectedAudioDevice } }
        : AUDIO_CONSTRAINTS;

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: audioConstraints,
      });

      engine.streamRef.current = stream;
      engine.setActiveStream(stream);

      const mimeType = getSupportedAudioMime();
      const recorderOptions: MediaRecorderOptions = mimeType
        ? { mimeType, audioBitsPerSecond: 128000 }
        : { audioBitsPerSecond: 128000 };

      const recorder = new MediaRecorder(stream, recorderOptions);
      engine.recorderRef.current = recorder;
      engine.chunksRef.current = [];

      recorder.ondataavailable = createOnDataAvailable(engine.chunksRef, "audio", options?.onDataAvailable);

      recorder.onstop = () => {
        engine.wavesurferMicrophoneRef.current?.stop();
        engine.stopTimer();
        engine.stopMicHealthMonitor();
        engine.setActiveStream(null);
        void engine.completeRecording(mimeType || "audio/webm");
      };

      // Block start until the live-transcription socket is ready.
      if (options?.onDataAvailable && options?.getSocketState) {
        try {
          await awaitSocketReady(options.getSocketState);
        } catch {
          const specificError = options.getSocketError?.();
          engine.setError(
            specificError && specificError.length > 0
              ? `Live transcription failed: ${specificError}`
              : "Live transcription socket failed to initialize. Please try again.",
          );
          stream.getTracks().forEach((t) => t.stop());
          engine.streamRef.current = null;
          engine.setActiveStream(null);
          return;
        }
      }

      recorder.start(1000); // 1s timeslices for streaming chunks
      engine.startTake("audio", mimeType || "audio/webm");
      engine.wavesurferMicrophoneRef.current?.start();
      engine.startTimer();
      engine.startMicHealthMonitor(stream);

      // MAX_RECORDING_SECONDS auto-stop is driven exclusively by engine.startTimer.
      // Do NOT add a redundant setTimeout, double-stop throws InvalidStateError.
      void MAX_RECORDING_SECONDS;
    } catch (err) {
      const message = getRecordingErrorMessage(err, false, "audio");
      if (message !== null) {
        engine.setError(message);
      }
    }
  }, [isActive, engine, options]);

  const stopRecording = useCallback(() => {
    if (!isActive) return;
    engine.stopMicHealthMonitor();
    setLocalError(null);
    if (
      engine.recorderRef.current &&
      engine.recorderRef.current.state !== "inactive"
    ) {
      engine.recorderRef.current.stop();
    }
    if (engine.streamRef.current) {
      engine.streamRef.current.getTracks().forEach((t) => t.stop());
      engine.streamRef.current = null;
    }
    engine.stopTimer();
  }, [isActive, engine]);

  if (!isActive) {
    return {
      ...engine,
      startRecording: async () => {},
      stopRecording: () => {},
    };
  }

  return {
    ...engine,
    error: localError ?? engine.error,
    startRecording,
    stopRecording,
  };
}
